import {
  createSlice,
  createAsyncThunk,
  isPending,
  isFulfilled,
  isRejected,
} from "@reduxjs/toolkit";
import http from "../../utils/axios";

// Fetch all talukas
export const fetchTalukas = createAsyncThunk(
  "talukas/fetchTalukas",
  async ({ page = 1, per_page = 15, status, search, district_uuid } = {}) => {
    const params = new URLSearchParams();
    params.append("page", page);
    params.append("per_page", per_page);
    if (status !== undefined) params.append("status", status);
    if (search) params.append("search", search);
    if (district_uuid) params.append("district_uuid", district_uuid);
    
    const res = await http.get(`/dashboard/talukas?${params.toString()}`);
    return res;
  }
);

// Create taluka
export const createTaluka = createAsyncThunk(
  "talukas/createTaluka",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await http.post("/dashboard/talukas", payload);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to create taluka"
      );
    }
  }
);

// Fetch taluka by UUID
export const fetchTalukaById = createAsyncThunk(
  "talukas/fetchTalukaById",
  async (uuid, { rejectWithValue }) => {
    try {
      const res = await http.get(`/dashboard/talukas/${uuid}`);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch taluka details"
      );
    }
  }
);

// Update taluka
export const updateTaluka = createAsyncThunk(
  "talukas/updateTaluka",
  async ({ uuid, data }, { rejectWithValue }) => {
    try {
      const res = await http.put(`/dashboard/talukas/${uuid}`, data);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to update taluka"
      );
    }
  }
);

// Delete taluka
export const deleteTaluka = createAsyncThunk(
  "talukas/deleteTaluka",
  async (uuid, { rejectWithValue }) => {
    try {
      const res = await http.delete(`/dashboard/talukas/${uuid}`);
      return { uuid, data: res };
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to delete taluka"
      );
    }
  }
);

// Toggle taluka status
export const toggleTalukaStatus = createAsyncThunk(
  "talukas/toggleTalukaStatus",
  async (uuid, { rejectWithValue }) => {
    try {
      const res = await http.patch(`/dashboard/talukas/${uuid}/toggle-status`);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to toggle taluka status"
      );
    }
  }
);

const talukasSlice = createSlice({
  name: "talukas",
  initialState: {
    talukas: [],
    pagination: null,
    taluka: null,
    talukaCreateStatus: "idle",
    talukaUpdateStatus: "idle",
    talukaDeleteStatus: "idle",
    talukaFetchStatus: "idle",
    talukaStatusChangeStatus: "idle",
    loading: false,
    error: null,
  },
  reducers: {
    resetTalukaStatuses: (state) => {
      state.talukaCreateStatus = "idle";
      state.talukaUpdateStatus = "idle";
      state.talukaDeleteStatus = "idle";
      state.talukaStatusChangeStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch talukas
      .addCase(fetchTalukas.fulfilled, (state, action) => {
        state.talukas = action.payload.data;
        state.pagination = action.payload.pagination;
      })

      // Create taluka
      .addCase(createTaluka.pending, (state) => {
        state.talukaCreateStatus = "loading";
      })
      .addCase(createTaluka.fulfilled, (state, action) => {
        state.talukaCreateStatus = "success";
        state.taluka = action.payload.data;
      })
      .addCase(createTaluka.rejected, (state) => {
        state.talukaCreateStatus = "failed";
      })

      // Fetch taluka by ID
      .addCase(fetchTalukaById.pending, (state) => {
        state.talukaFetchStatus = "loading";
      })
      .addCase(fetchTalukaById.fulfilled, (state, action) => {
        state.talukaFetchStatus = "success";
        state.taluka = action.payload.data;
      })
      .addCase(fetchTalukaById.rejected, (state) => {
        state.talukaFetchStatus = "failed";
        state.taluka = null;
      })

      // Update taluka
      .addCase(updateTaluka.pending, (state) => {
        state.talukaUpdateStatus = "loading";
      })
      .addCase(updateTaluka.fulfilled, (state, action) => {
        state.talukaUpdateStatus = "success";
        const index = state.talukas.findIndex(
          (tal) => tal.uuid === action.meta.arg.uuid
        );
        if (index !== -1) {
          state.talukas[index] = action.payload.data;
        }
        state.taluka = action.payload.data;
      })
      .addCase(updateTaluka.rejected, (state) => {
        state.talukaUpdateStatus = "failed";
      })

      // Delete taluka
      .addCase(deleteTaluka.pending, (state) => {
        state.talukaDeleteStatus = "loading";
      })
      .addCase(deleteTaluka.fulfilled, (state, action) => {
        state.talukaDeleteStatus = "success";
        state.talukas = state.talukas.filter(
          (tal) => tal.uuid !== action.payload.uuid
        );
      })
      .addCase(deleteTaluka.rejected, (state) => {
        state.talukaDeleteStatus = "failed";
      })

      // Toggle status
      .addCase(toggleTalukaStatus.pending, (state) => {
        state.talukaStatusChangeStatus = "loading";
      })
      .addCase(toggleTalukaStatus.fulfilled, (state, action) => {
        state.talukaStatusChangeStatus = "success";
        const index = state.talukas.findIndex(
          (tal) => tal.uuid === action.meta.arg
        );
        if (index !== -1) {
          state.talukas[index] = action.payload.data;
        }
      })
      .addCase(toggleTalukaStatus.rejected, (state) => {
        state.talukaStatusChangeStatus = "failed";
      })

      .addMatcher(isPending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addMatcher(isFulfilled, (state) => {
        state.loading = false;
      })
      .addMatcher(isRejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export const { resetTalukaStatuses } = talukasSlice.actions;
export default talukasSlice.reducer;
